import { useEffect, useRef, useState } from 'react';
import { Icon } from '@sakhtar/design-system/react';

export interface RowMenuItem {
  readonly label: string;
  readonly onSelect: () => void;
  readonly danger?: boolean;
  /** Shown disabled with this reason instead of being hidden. */
  readonly disabledReason?: string | null;
}

/**
 * The row's «…» menu — every action other than opening the record lives
 * here (Sales CRM design, shared table rules). Clicks inside never bubble
 * to the row's own open-detail handler.
 */
export function RowMenu({
  items,
  label = 'عملیات',
}: {
  items: readonly RowMenuItem[];
  label?: string;
}) {
  const [open, setOpen] = useState(false);
  const [position, setPosition] = useState<{ top: number; left: number } | null>(null);
  const wrapRef = useRef<HTMLSpanElement | null>(null);
  const buttonRef = useRef<HTMLButtonElement | null>(null);

  // Fixed to the viewport, measured from the button: a table inside a
  // horizontal scroll container would otherwise clip the menu, and the
  // «…» column sits at the inline-end edge, so the menu opens inward.
  function toggle() {
    if (open) {
      setOpen(false);
      return;
    }
    const rect = buttonRef.current?.getBoundingClientRect();
    if (rect) {
      const menuWidth = 200;
      const menuHeight = 38 * items.length + 12;
      const below = rect.bottom + 4;
      const top = below + menuHeight > window.innerHeight ? rect.top - menuHeight - 4 : below;
      const left = Math.min(Math.max(8, rect.left), window.innerWidth - menuWidth - 8);
      setPosition({ top: Math.max(8, top), left });
    }
    setOpen(true);
  }

  useEffect(() => {
    if (!open) return;
    const onDoc = (event: MouseEvent) => {
      if (!wrapRef.current?.contains(event.target as Node)) setOpen(false);
    };
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false);
    };
    const onScroll = () => setOpen(false);
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    window.addEventListener('scroll', onScroll, true);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
      window.removeEventListener('scroll', onScroll, true);
    };
  }, [open]);

  if (items.length === 0) return null;
  return (
    <span
      ref={wrapRef}
      className="sk-menu-wrap"
      onClick={(event) => event.stopPropagation()}
      onKeyDown={(event) => event.stopPropagation()}
    >
      <button
        ref={buttonRef}
        type="button"
        className="sk-btn sk-btn--ghost sk-btn--icon sk-btn--sm"
        aria-label={label}
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={toggle}
      >
        <Icon name="more" size="sm" />
      </button>
      {open ? (
        <div
          className="sk-menu"
          role="menu"
          style={{
            minWidth: 190,
            position: 'fixed',
            top: position?.top ?? 0,
            left: position?.left ?? 0,
            // Physical sides only: in RTL a logical `inset-inline-end` is
            // `left`, which would cancel the measured position.
            right: 'auto',
            bottom: 'auto',
            zIndex: 'var(--sk-z-popover, 60)' as unknown as number,
          }}
        >
          {items.map((item) => (
            <button
              key={item.label}
              type="button"
              role="menuitem"
              className="sk-menu-item"
              disabled={Boolean(item.disabledReason)}
              title={item.disabledReason ?? undefined}
              style={{
                width: '100%',
                ...(item.danger ? { color: 'var(--sk-danger)' } : {}),
                ...(item.disabledReason ? { opacity: 0.5, cursor: 'not-allowed' } : {}),
              }}
              onClick={() => {
                setOpen(false);
                item.onSelect();
              }}
            >
              {item.label}
            </button>
          ))}
        </div>
      ) : null}
    </span>
  );
}
